/**
 * Workers API のクライアント。
 *
 * カタログの読み取りは PostgREST（RLS 任せ）で直接行い、
 * 「権利チェックが要る操作」だけをこの API 経由にしている。
 */
import { supabase } from './supabase.ts'
import type { UploadSession, BoxPart, UploadResult } from './chunkedUpload.ts'
import type { Role } from './roles.ts'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message?: string) {
    super(message ?? code)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }

  /**
   * 再試行しても結果が変わらないか。
   *
   * 入力が悪い（400 など）のに再試行を続けると、画面には何も出ないまま
   * 延々とリクエストを投げ続けることになる。
   * 429 は待てば通るので恒久失敗には含めない。
   */
  get permanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 429
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new ApiError(401, 'not_signed_in', 'ログインしていません')
  return { Authorization: `Bearer ${token}` }
}

async function call<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      ...(await authHeaders()),
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(rest.headers as Record<string, string> | undefined),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, body.error ?? 'unknown_error')
  }
  return res.json() as Promise<T>
}

/**
 * ログインは通ったがプロフィールが無いときに、招待を引き当て直す。
 *
 * 冪等。招待が見つからない場合は認証に使われたメールアドレスが返るので、
 * 「招待したのと違うアドレスでログインしている」を画面で指摘できる。
 */
import type { TranscodeJob } from './transcodeStatus.ts'

/**
 * 素材ごとの変換状況を取る。
 *
 * 実行中のジョブについては Worker がコンテナに実進捗を聞きにいくので、
 * 呼ぶたびに往復が増える。**進行中のものが無くなったら呼ぶのをやめること。**
 */
export type EmailProvider = 'supabase' | 'cloudflare' | 'resend' | 'sendgrid'

export type EmailSettings = {
  provider: EmailProvider
  fromAddress: string | null
  fromName: string | null
  cloudflareAccountId: string | null
  /** APIキーが保存済みか。値そのものは返らない。 */
  hasApiKey: boolean
  apiKeyHint: string | null
  lastTestAt: string | null
  lastTestOk: boolean | null
  lastTestError: string | null
  updatedAt: string | null
}

export const getEmailSettings = () =>
  call<{ settings: EmailSettings }>('/v1/admin/email')

export const updateEmailSettings = (patch: {
  provider?: EmailProvider
  fromAddress?: string
  fromName?: string
  cloudflareAccountId?: string
  /** 省略すれば既存の値を保持する。空文字で削除。 */
  apiKey?: string
}) => call<{ settings: EmailSettings }>('/v1/admin/email', { method: 'POST', json: patch })

export const testEmailSettings = (to?: string) =>
  call<{ ok: boolean; to: string; detail: string | null; settings: EmailSettings }>(
    '/v1/admin/email/test',
    { method: 'POST', json: to ? { to } : {} },
  )

export type WatermarkSettings = {
  label: string
  position:
    | 'top-left' | 'top-center' | 'top-right'
    | 'middle-left' | 'middle-center' | 'middle-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right'
  /** 画像高さに対する文字サイズの比率。 */
  font_scale: number | string
  opacity: number | string
  box_enabled: boolean
  box_opacity: number | string
  timecode_enabled: boolean
  apply_to_images: boolean
  updated_at: string
}

export const getWatermarkSettings = () =>
  call<{ settings: WatermarkSettings | null }>('/v1/admin/watermark')

export const updateWatermarkSettings = (patch: {
  label?: string
  position?: string
  fontScale?: number
  opacity?: number
  boxEnabled?: boolean
  boxOpacity?: number
  timecodeEnabled?: boolean
  applyToImages?: boolean
}) =>
  call<{ settings: WatermarkSettings | null }>('/v1/admin/watermark', {
    method: 'POST',
    json: patch,
  })

/** 詳細表示用プレビューの署名付きURL（写真の拡大表示に使う）。 */
export const getPreviewUrls = (assetIds: string[]) =>
  call<{ urls: Record<string, string> }>('/v1/thumbnails/preview-urls', {
    method: 'POST',
    json: { assetIds },
  })

/** Box の representations から詳細表示用プレビューを作る。 */
export const createPreviewFromBox = (assetId: string) =>
  call<{ status: string; key?: string; retryAfter?: number }>(
    '/v1/thumbnails/preview-from-box',
    { method: 'POST', json: { assetId } },
  )

/** 小容量マスター（写真など）を Box へ直接送るためのトークン。 */
export const createDirectUploadToken = (params: { categoryId: string; fileSize: number }) =>
  call<{ accessToken: string; expiresAt: number; folderId: string; uploadUrl: string }>(
    '/v1/uploads/master/direct-token',
    { method: 'POST', json: params },
  )

/** 素材のカテゴリを変更する（Box のフォルダも移動する）。 */
export const moveAssetCategory = (assetId: string, categoryId: string) =>
  call<{ moved: boolean; categoryId: string }>(`/v1/assets/${assetId}/category`, {
    method: 'POST',
    json: { categoryId },
  })

/** 素材を削除する（Box はゴミ箱へ、R2 の派生物は削除）。 */
export const deleteAsset = (assetId: string) =>
  call<{ deleted: boolean; boxTrashed: boolean; r2Purged: boolean; note: string }>(
    `/v1/assets/${assetId}`,
    { method: 'DELETE' },
  )

/** メタデータを抽出して保存する。 */
export const extractMetadata = (assetId: string) =>
  call<{
    extracted: boolean
    technical: Record<string, unknown>
    capturedAt: string | null
    hasLocation: boolean
    preview: { bytes?: number; width?: number; height?: number; error?: string } | null
  }>(`/v1/assets/${assetId}/metadata`, { method: 'POST' })

export type AccessLog = {
  id: number
  created_at: string
  kind: string
  outcome: 'granted' | 'denied'
  denial_reason: string | null
  asset_id: string | null
  asset_title: string | null
  asset_file_name: string | null
  byte_size: number | null
  ip: string | null
  user_agent: string | null
  organizations: { name: string } | null
  users: { email: string; display_name: string | null } | null
}

/** アクセスログ（監査証跡）を読む。 */
export const getAccessLogs = (params: { limit?: number; outcome?: string } = {}) => {
  const q = new URLSearchParams()
  if (params.limit) q.set('limit', String(params.limit))
  if (params.outcome) q.set('outcome', params.outcome)
  return call<{ logs: AccessLog[] }>(`/v1/admin/access-logs?${q}`)
}

export type AdminUser = {
  id: string
  email: string
  display_name: string | null
  role: Role
  org_id: string | null
  org_name: string | null
  is_active: boolean
  /** auth アカウントに紐づいているか。false は招待未消化か認証解除済み。 */
  has_login: boolean
  /** 'sync' は CRM 同期の管理下。ロールと在籍は CRM が正で、ここからのロール変更は 409 になる。 */
  managed_by: ManagedBy
  synced_at: string | null
  /**
   * 非 null の間、CRM 同期はこのユーザーを勝手に再開・変更しない
   * （'staff'＝スタッフが手動で停止した印。再開すると null に戻る）。
   */
  hold_reason: string | null
  /** CRM 側の担当者ID。手動管理のユーザーは null。 */
  external_ref: string | null
  created_at: string
}

/** 全ユーザー（運営スタッフ＋顧客）の一覧。customer.manage 権限が要る。 */
export const listUsers = () => call<{ users: AdminUser[] }>('/v1/admin/users')

/**
 * ロールを変更する。
 *
 * 運営側⇔顧客側をまたぐ変更はサーバが 400 'cross_side_change_not_allowed' で
 * 弾く（退会→再招待で対応）。最後の有効な最高管理者の降格は 409。
 */
export const updateUserRole = (id: string, role: Role) =>
  call<{ id: string; role: Role }>(`/v1/admin/users/${id}/role`, { method: 'POST', json: { role } })

/**
 * 有効/停止を切り替える。停止時はログインセッションも失効する。
 *
 * sessions_revoked は「失効まで確認できたか」。停止自体は成功していても
 * 失効APIが失敗すれば false が返る（サーバは握り潰さずここまで通す）。
 * 対象がまだ一度もログインしていない場合は null。
 */
export const updateUserStatus = (id: string, active: boolean) =>
  call<{ id: string; is_active: boolean; sessions_revoked: boolean | null }>(
    `/v1/admin/users/${id}/status`,
    { method: 'POST', json: { active } },
  )

export type AuditLog = {
  id: number
  created_at: string
  action: string
  target_table: string
  target_id: string | null
  /** 変更内容の要約（JSON）。形は action ごとに異なる。 */
  detail: Record<string, unknown> | null
  request_id: string | null
  /** 操作者。service_role 経由などで解決できない場合は null。 */
  users: { email: string; display_name: string | null } | null
}

/** 操作ログ（audit_logs・追記専用）を読む。logs.read 権限が要る。 */
export const listAuditLogs = (
  params: { limit?: number; action?: string; targetTable?: string } = {},
) => {
  const q = new URLSearchParams()
  if (params.limit) q.set('limit', String(params.limit))
  if (params.action) q.set('action', params.action)
  if (params.targetTable) q.set('targetTable', params.targetTable)
  return call<{ logs: AuditLog[] }>(`/v1/admin/audit-logs?${q}`)
}

/** 同期の管理元。'sync' の行は CRM が正で、この画面からの変更は制限される。 */
export type ManagedBy = 'manual' | 'sync'

export type SyncOrg = {
  id: string
  name: string
  status: string
  external_ref: string | null
  managed_by: ManagedBy
  synced_at: string | null
  crm_url: string | null
}

export type SyncStatus = {
  enabled: boolean
  max_deactivations: number
  max_deactivation_pct: number
  pending_holds: number
  last_event_at: string | null
  events_24h: number
  rejected_24h: number
  synced_orgs: number
  synced_users: number
  /** 全組織（手動管理も含む）。ひも付け画面の選択肢に使う。 */
  orgs: SyncOrg[]
}

export type SyncEvent = {
  id: number
  event_id: string
  source: string
  type: string
  /** received / applied / held / rejected / disabled。 */
  outcome: string
  /** 形は type / outcome ごとに異なる。画面では防御的に読む。 */
  detail: Record<string, unknown>
  received_at: string
}

export type SyncHold = {
  id: string
  kind: 'email_change' | 'mass_deactivation'
  status: 'pending' | 'approved' | 'rejected' | 'superseded'
  /** 形は kind ごとに異なる（DB 実装依存）。画面では防御的に読む。 */
  data: Record<string, unknown>
  event_id: string | null
  created_at: string
  resolved_at: string | null
  user: { id: string; email: string; display_name: string | null } | null
  org: { id: string; name: string } | null
}

/** resolve_sync_hold の戻り。kind によって入る項目が変わる。 */
export type SyncHoldResult = {
  ok: boolean
  kind?: 'email_change' | 'mass_deactivation'
  old_email?: string
  new_email?: string
  deactivated?: { user_id: string; auth_user_id: string | null }[]
  suspended_orgs?: string[]
  error?: string
}

export type SyncSettings = {
  enabled: boolean
  max_deactivations: number
  max_deactivation_pct: number
  updated_at: string | null
}

/** CRM 同期の全体状況。customer.manage 権限が要る（以下の sync 系はすべて同様）。 */
export const getSyncStatus = () => call<{ status: SyncStatus }>('/v1/admin/sync/status')

export const listSyncEvents = (limit = 50) =>
  call<{ events: SyncEvent[] }>(`/v1/admin/sync/events?limit=${limit}`)

export const listSyncHolds = (status = 'pending') =>
  call<{ holds: SyncHold[] }>(`/v1/admin/sync/holds?status=${encodeURIComponent(status)}`)

/**
 * 保留を承認して適用する。email_change は Auth 側のログイン用アドレスも一緒に変わる。
 * すでに処理済みの保留は 409 'hold_not_pending'（画面を読み込み直せば消える）。
 */
export const approveSyncHold = (id: string) =>
  call<{ result: SyncHoldResult }>(`/v1/admin/sync/holds/${id}/approve`, {
    method: 'POST',
    json: {},
  })

/** 却下。CRM 側のデータを直さない限り、次のスナップショットで同じ保留が再出現する（仕様）。 */
export const rejectSyncHold = (id: string) =>
  call<{ result: SyncHoldResult }>(`/v1/admin/sync/holds/${id}/reject`, {
    method: 'POST',
    json: {},
  })

/**
 * 手動管理の組織を CRM の顧客とひも付ける。以後は同期が正となり上書きする。
 * ひも付け済みは 409 'already_linked'、ID の重複は 409 'external_ref_in_use'。
 */
export const linkOrgExternalRef = (params: { orgId: string; externalRef: string; crmUrl?: string }) =>
  call<{ org: SyncOrg }>('/v1/admin/sync/link-org', { method: 'POST', json: params })

export const updateSyncSettings = (patch: {
  enabled?: boolean
  maxDeactivations?: number
  maxDeactivationPct?: number
}) => call<{ settings: SyncSettings }>('/v1/admin/sync/settings', { method: 'POST', json: patch })

/** 変換を投入する。実処理はキューが行うので、これは即座に返る。 */
export const enqueueTranscode = (params: {
  assetIds?: string[]
  all?: boolean
  /** 試行回数の上限に達したジョブを再開する。原因を直したときだけ使う。 */
  resetAttempts?: boolean
}) =>
  call<{ enqueued: number; assetIds: string[] }>('/v1/transcode/enqueue', {
    method: 'POST',
    json: params,
  })

/** Worker 側の上限（MAX_BULK）。これを超えると 400 で弾かれる。 */
const TRANSCODE_STATUS_CHUNK = 50

export const getTranscodeStatus = async (assetIds: string[]) => {
  // 未変換の素材が50件を超えると 400 になり、進捗が**一切**出なくなる。
  // 分割して問い合わせる。
  const chunks: string[][] = []
  for (let i = 0; i < assetIds.length; i += TRANSCODE_STATUS_CHUNK) {
    chunks.push(assetIds.slice(i, i + TRANSCODE_STATUS_CHUNK))
  }
  const results = await Promise.all(
    chunks.map((ids) =>
      call<{ jobs: TranscodeJob[] }>(`/v1/transcode/status?assetIds=${ids.join(',')}`),
    ),
  )
  return { jobs: results.flatMap((r) => r.jobs) }
}

export type ClaimResult = {
  status: 'already_linked' | 'relinked' | 'created' | 'not_invited' | 'auth_user_not_found'
  linked: boolean
  email: string | null
  role: string | null
}

export const claimInvitation = () => call<ClaimResult>('/session/claim', { method: 'POST' })

export type DownloadInfo = {
  downloadUrl: string
  fileName: string | null
  sha1: string | null
  byteSize: number | null
  licenseId: string
  usageScope: string
  validUntil: string | null
  downloadsUsed: number
  maxDownloads: number | null
}

/**
 * 元本ダウンロードURLを取得する。
 *
 * `redirect=false` を使って JSON で受け取り、SHA-1 などの納品情報を
 * 画面に出せるようにしている。実際の転送はブラウザ ⇔ Box で行われ、
 * Worker はバイト列を中継しない。
 */
export const getDownloadInfo = (assetId: string) =>
  call<DownloadInfo>(`/v1/assets/${assetId}/download-url?redirect=false`)

export type LightUploadTarget = {
  uploadUrl: string
  method: 'PUT'
  key: string
  bucket: string
  expiresIn: number
  headers: Record<string, string>
}

export const createLightUploadUrl = (params: {
  assetId: string
  kind: 'thumbnail' | 'poster'
  contentType: string
  byteSize: number
}) => call<LightUploadTarget>('/v1/uploads/light-url', { method: 'POST', json: params })

export type MasterSession = {
  session: UploadSession
  accessToken: string
  accessTokenExpiresAt: string
  restrictedTo: unknown
  category: { id: string; name: string; boxFolderId: string }
}

export const createMasterSession = (params: {
  categoryId: string
  fileName: string
  fileSize: number
}) => call<MasterSession>('/v1/uploads/master/session', { method: 'POST', json: params })

/**
 * 分割アップロードの確定を Worker に代行させる。
 *
 * ブラウザから Box の commit を直接叩くと、Box の CORS 許可オリジンに
 * 登録されていない限り 403 になる。Pages のプレビューURLはデプロイごとに
 * 変わるため登録し切れない。commit は数KBの JSON なので Worker が代行しても
 * 「実データを中継しない」原則は保たれる。
 */
export async function commitMasterUpload(args: {
  session: UploadSession
  parts: BoxPart[]
  fileSha1Base64: string
}): Promise<UploadResult> {
  for (let attempt = 0; attempt < 30; attempt++) {
    const res = await fetch(`${BASE_URL}/v1/uploads/master/commit`, {
      method: 'POST',
      headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        commitUrl: args.session.endpoints.commit,
        parts: args.parts,
        fileSha1Base64: args.fileSha1Base64,
      }),
    })

    // Box がまだ処理中。同じ内容で再送する（202 は正常系）。
    if (res.status === 202) {
      const body = (await res.json()) as { retryAfter?: number }
      await new Promise((r) => setTimeout(r, Math.max(1, body.retryAfter ?? 2) * 1000))
      continue
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      throw new ApiError(res.status, body.error ?? 'commit_failed')
    }

    const body = (await res.json()) as {
      file: { id: string; name: string; size: number; sha1: string }
    }
    return {
      fileId: body.file.id,
      fileName: body.file.name,
      size: body.file.size,
      sha1: body.file.sha1,
    }
  }
  throw new ApiError(504, 'commit_timeout', 'Box の確定処理が完了しませんでした')
}

export const getThumbnailUrls = (assetIds: string[]) =>
  call<{ urls: Record<string, string>; expiresIn: number }>('/v1/thumbnails/urls', {
    method: 'POST',
    json: { assetIds },
  })

/**
 * Box が持っているプレビュー画像からサムネイルを作り直す。
 *
 * 元動画の再アップロードが要らないのが利点。Box 側で生成中（202）の場合は
 * 指示された秒数だけ待って再試行する。
 */
export async function regenerateThumbnailFromBox(assetId: string): Promise<{
  key: string
  bytes: number
}> {
  for (let attempt = 0; attempt < 12; attempt++) {
    const res = await fetch(`${BASE_URL}/v1/thumbnails/from-box`, {
      method: 'POST',
      headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetId }),
    })

    if (res.status === 202) {
      const body = (await res.json()) as { retryAfter?: number }
      await new Promise((r) => setTimeout(r, Math.max(1, body.retryAfter ?? 5) * 1000))
      continue
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      throw new ApiError(res.status, body.error ?? 'thumbnail_failed')
    }
    return (await res.json()) as { key: string; bytes: number }
  }
  throw new ApiError(504, 'thumbnail_timeout', 'Box 側の生成が終わりませんでした')
}

/** 透かし入りプロキシの再生用URL（15分で失効）。 */
export const getProxyUrl = (assetId: string) =>
  call<{ url: string; expiresIn: number }>(`/v1/assets/${assetId}/proxy-url`)

export type Invitation = {
  id: string
  email: string
  role: Role
  org_name: string | null
  expires_at: string
  created_at: string
  /** 招待の消化時刻。リンク発行でも埋まるので「ログインした証拠にはならない」。 */
  accepted_at: string | null
  /** 実際にログインが成立した時刻。null なら誰もリンクを踏んでいない。 */
  last_sign_in_at: string | null
  has_auth_account: boolean
  /** false だとログインできても「アクセス権がありません」になる。 */
  has_profile: boolean
}

export const listInvitations = () =>
  call<{ invitations: Invitation[] }>('/v1/admin/invitations')

export const createInvitation = (params: {
  email: string
  role: Role
  /** 顧客側ロールでは必須。運営スタッフは組織に属さないので送らない。 */
  orgId?: string
}) => call<{ invitation: { id: string } }>('/v1/admin/invitations', { method: 'POST', json: params })

/**
 * 招待を取り消す。
 *
 * すでに受理された招待（プロフィール作成済み）は 409 'already_accepted' で
 * 弾かれる。その場合はアカウント側で停止する。
 */
export const deleteInvitation = (id: string) =>
  call<{ deleted: boolean }>(`/v1/admin/invitations/${id}`, { method: 'DELETE' })

/**
 * ログインリンクを発行する。
 *
 * 認証の迂回ではなく、Supabase が正規に発行する1回限りのマジックリンク。
 * メール送信の制限（2通/時）を受けずに配れるので、デモや初回導入で使う。
 */
/**
 * カテゴリを追加する。
 *
 * カテゴリは Box のフォルダと 1:1 対応するため、Worker 側で
 * Box フォルダの作成とレコード作成をまとめて行う。
 */
export const createCategory = (params: {
  name: string
  slug: string
  /** 親カテゴリ。省略・null で最上位。最大3階層。 */
  parentId?: string | null
  sortOrder?: number
}) =>
  call<{ category: { id: string } | null; boxFolderId: string; reusedExistingFolder: boolean }>(
    '/v1/admin/categories',
    { method: 'POST', json: params },
  )

/**
 * カテゴリの改名・移動・並び順。Box のフォルダも改名・移動される。
 * PATCH ではなく POST なのは API の CORS 許可メソッドに合わせているため。
 */
export const updateCategory = (
  id: string,
  params: { name?: string; parentId?: string | null; sortOrder?: number },
) =>
  call<{ updated: boolean; moved: boolean; renamed: boolean }>(`/v1/admin/categories/${id}`, {
    method: 'POST',
    json: params,
  })

/** 空のカテゴリの削除。DB（子・素材）と Box（フォルダの中身）の両方が空のときだけ通る。 */
export const deleteCategory = (id: string) =>
  call<{ deleted: boolean; boxFolder: 'deleted' | 'not_found' | 'not_empty' | 'error' }>(
    `/v1/admin/categories/${id}`,
    { method: 'DELETE' },
  )

export type ReconcileIssue =
  | { kind: 'folder_missing_in_box'; categoryId: string; name: string; boxFolderId: string }
  | { kind: 'name_mismatch'; categoryId: string; name: string; boxFolderId: string; boxName: string }
  | {
      kind: 'parent_mismatch'
      categoryId: string
      name: string
      boxFolderId: string
      expectedParentFolderId: string
      boxParentFolderId: string
    }
  | {
      kind: 'files_in_branch'
      categoryId: string
      name: string
      boxFolderId: string
      count: number
      files: { id: string; name: string }[]
    }
  | { kind: 'unregistered_folder'; boxFolderId: string; name: string; boxParentFolderId: string; fileCount: number }
  | { kind: 'files_in_root'; count: number; files: { id: string; name: string }[] }
  | { kind: 'asset_uncategorized'; assetId: string; title: string; boxFolderId: string | null }
  | {
      kind: 'asset_folder_mismatch'
      assetId: string
      title: string
      categoryId: string
      expectedFolderId: string
      actualFolderId: string
    }
  | {
      kind: 'unregistered_files'
      categoryId: string
      name: string
      boxFolderId: string
      count: number
      files: { id: string; name: string }[]
    }

/** DB のカテゴリの木と Box のフォルダの木を突き合わせ、ずれを列挙する。 */
export const reconcileCategories = () =>
  call<{ checkedAt: string; categories: number; boxFolders: number; issues: ReconcileIssue[] }>(
    '/v1/admin/categories/reconcile',
  )

/**
 * カスタム項目の値を更新する。null / 空は「未設定」としてキーが消える。
 * 受け付けるのは素材のカテゴリに有効な項目だけ（Worker が型と選択肢を検証する）。
 */
export const updateAssetAttributes = (assetId: string, values: Record<string, unknown>) =>
  call<{ attributes: Record<string, unknown> }>(`/v1/assets/${assetId}/attributes`, {
    method: 'POST',
    json: { values },
  })

export const issueLoginLink = (email: string) =>
  call<{ email: string; loginUrl: string; code: string | null; note: string }>(
    '/v1/admin/login-link',
    { method: 'POST', json: { email } },
  )

export type ImportResult = {
  imported: number
  skipped: number
  /** 枝（子を持つ）カテゴリのフォルダにあったため、未分類で登録した件数（imported に含む）。 */
  quarantined: number
  categories: {
    categoryId: string
    categoryName: string
    found: number
    imported: number
    skipped: number
    quarantined: number
    titles: string[]
  }[]
}

/**
 * Box のフォルダを走査して素材レコードを作る。
 * 既に取り込み済みのファイルは飛ばすので、何度実行しても増えない。
 */
export const importAssetsFromBox = (categoryId?: string) =>
  call<ImportResult>('/v1/imports/from-box', {
    method: 'POST',
    json: categoryId ? { categoryId } : {},
  })

/** 古いサムネイルを R2 から掃除する。 */
export const pruneThumbnails = (assetId: string) =>
  call<{ deleted: number; kept: string | null }>('/v1/thumbnails/prune', {
    method: 'POST',
    json: { assetId },
  })

export const getMe = () =>
  call<{ id: string; email: string; displayName: string | null; role: string; orgId: string | null }>(
    '/v1/me',
  )

/**
 * 署名付きURLへ直接 PUT する（Worker を経由しない）。
 *
 * PUT はプリフライトを伴うため、**R2 バケット側に CORS ポリシーが必要**。
 * 未設定だと fetch がネットワークエラー（Safari では "Load failed"、
 * Chrome では "Failed to fetch"）になり、原因が分からない。
 * 設定コマンドは apps/api/README.md を参照。
 */
export async function putToSignedUrl(
  target: LightUploadTarget,
  blob: Blob,
): Promise<void> {
  let res: Response
  try {
    res = await fetch(target.uploadUrl, {
      method: target.method,
      headers: target.headers,
      body: blob,
    })
  } catch (e) {
    // レスポンスすら返らない＝ほぼ CORS プリフライトの失敗
    throw new ApiError(
      0,
      'r2_network_error',
      'R2 への送信がネットワークレベルで失敗しました。' +
        'R2 バケットの CORS 設定を確認してください' +
        `（wrangler r2 bucket cors list ${target.bucket}）。詳細: ${
          e instanceof Error ? e.message : String(e)
        }`,
    )
  }
  if (!res.ok) {
    throw new ApiError(res.status, 'upload_failed', `R2 へのアップロードに失敗しました (${res.status})`)
  }
}
